// src/features/mech/casting/questions/CastingQuestions.jsx
import React, { useState, useRef } from "react";
import QuestionsSection from "../../../../shared/sections/QuestionsSection";
import PartNameQuestion from "./PartNameQuestion";
import MaterialSelectionQuestion from "./MaterialSelectionQuestion";
import MaterialGradeCastingQuestion from "./MaterialGradeCastingQuestion";
import BatchSizeQuestion from "./BatchSizeQuestion";
import PartWeightDimensionsQuestion from "./PartWeightDimensionsQuestion";
import PartDetailsQuestion from "./PartDetailsQuestion";
import ManufacturingCountrySelect from "./ManufacturingCountrySelect";
import ProcessInvolved from "./ProcessInvolved";

/**
 * CastingQuestionsSection Component
 *
 * Encapsulates all product-related questions for Cookies commodity
 * Manages form data, validation, and question flow
 *
 * @param {function} onComplete - Callback when all questions are answered
 * @param {function} onDataChange - Callback when form data changes
 * @param {function} onDemoModeChange - Callback when demo mode changes
 * @param {object} initialData - Initial data to populate the form
 * @param {function} onReset - Callback when the form is reset
 */
export default function CastingQuestions({ onComplete, onDataChange, onDemoModeChange, onReset, initialData }) {
  const [productFormData, setProductFormData] = useState(
    initialData || {
      productName: "",
      materialSelection: "",
      materialGrade: "",
      batchSize: "",
      partWeightDimensions: {
        finishWeight: "",
        partLength: "",
        partWidth: "",
        partHeight: "",
      },
      partDetails: {
        // regrindPct: "",
        avgWallThickness: "",
        // runnerType: "",
        noOfCavities: "",
      },
      processInvolved: {
        sandBlasting: false,
        deburing: false,
        heatTreatment: false
      },
      // marketPositioning: "",
      // skuWeight: "",
      // skuWeightUnit: "g",
      annualVolume: "",
      manufacturingCountry: "",
    }
  );

  // State to track material validation status
  const [isMaterialValid, setIsMaterialValid] = useState(false);
  const [isValidatingMaterial, setIsValidatingMaterial] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  
  // Calculate initial step based on answered questions
  
  const getInitialStep = () => {
    if (!initialData) return 0;

    if (initialData.manufacturingCountry) return 6; // Last question
    if (initialData.partDetails) return 5;
    if (initialData.partWeightDimensions) return 4;
    if (initialData.batchSize) return 3;
    if (initialData.materialGrade) return 2; // Material Grade
    if (initialData.materialSelection) return 1;
    if (initialData.productName) return 0;
    return 0;
  };

  const [productChatStep, setProductChatStep] = useState(getInitialStep());
  
  // Refs for validation
  const materialSelectionRef = useRef(null);
  const materialGradeRef = useRef(null);

  // Check if all questions are answered
  const isAllAnswered =
    productFormData.productName?.trim() &&
    productFormData.materialSelection?.trim() &&
    productFormData.batchSize &&
    parseFloat(productFormData.batchSize) > 0 &&
    productFormData.partWeightDimensions?.finishWeight &&
    productFormData.partWeightDimensions?.partLength &&
    productFormData.partWeightDimensions?.partWidth &&
    productFormData.partWeightDimensions?.partHeight &&
    productFormData.manufacturingCountry;

  // Notify parent when data changes and all questions are answered
  React.useEffect(() => {
    if (isAllAnswered) {
      onDataChange?.(productFormData);
    }
    // Don't call onDataChange(null) when incomplete - this would wipe parent state
    // Parent should retain the last valid data for persistence across navigation
  }, [productFormData, isAllAnswered, onDataChange]);

  // Selected values for display - only show values that have been answered
  const productSelectedValues = [
    productFormData.productName && {
      label: "Part Name:",
      value: productFormData.productName,
    },
    productFormData.materialSelection && {
      label: "Material:",
      // Show Material Grade if selected, otherwise show base material
      value: productFormData.materialSelection + (productFormData.materialGrade?"-"+productFormData.materialGrade:""),
    },
    productFormData.batchSize && {
      label: "Batch Size:",
      value: productFormData.batchSize,
    },
    // Only show Part Dimensions after step 3 (Part Weight Dimensions question) is answered
    productFormData.partWeightDimensions?.finishWeight &&
      productFormData.partWeightDimensions?.partLength &&
      productFormData.partWeightDimensions?.partWidth &&
      productFormData.partWeightDimensions?.partHeight && {
        label: "Part Dimensions:",
        value: `L: ${productFormData.partWeightDimensions.partLength}mm, W: ${
          productFormData.partWeightDimensions.partWidth
        }mm, H: ${productFormData.partWeightDimensions.partHeight}mm, Weight: ${
          productFormData.partWeightDimensions.finishWeight
        }g`,
      },
    // Show Part Details if any field has a value
    (productFormData.partDetails?.regrindPct ||
      productFormData.partDetails?.avgWallThickness ||
      productFormData.partDetails?.runnerType ||
      productFormData.partDetails?.noOfCavities) && {
      label: "Part Details:",
      value: [
        // productFormData.partDetails?.regrindPct && `Regrind: ${productFormData.partDetails.regrindPct}%`,
        productFormData.partDetails?.avgWallThickness &&
          `Wall: ${productFormData.partDetails.avgWallThickness}mm`,
        // productFormData.partDetails?.runnerType && `Runner: ${productFormData.partDetails.runnerType}`,
        productFormData.partDetails?.noOfCavities &&
          `Cavities: ${productFormData.partDetails.noOfCavities}`,
      ]
        .filter(Boolean)
        .join(", "),
    },
    // Show Process Involved if any field has a value
    (productFormData.processInvolved?.heatTreatment ||
      productFormData.processInvolved?.deburing ||
      productFormData.processInvolved?.sandBlasting) && {
      label: "Process Involved:",
      value: [
        productFormData.processInvolved?.sandBlasting &&
          `Sand Blasting`,
        productFormData.processInvolved?.deburing &&
          `Deburring`,
        productFormData.processInvolved?.heatTreatment &&
          `Heat Treatment`,
        
      ]
        .filter(Boolean)
        .join(", "),
    },
    productFormData.manufacturingCountry && {
      label: "Manufacturing Country:",
      value: productFormData.manufacturingCountry,
    },
  ].filter(Boolean); // Remove falsy values

  // Validation function to check if next button should be disabled
  const isNextDisabled = (step) => {
    switch (step) {
      case 0: // Product Name
        return !productFormData.productName || productFormData.productName.trim() === "";
      case 1: // Material Selection
        return !productFormData.materialSelection || productFormData.materialSelection.trim() === "";
      case 2: // Material Grade
        return false; // Material Grade is optional, so always allow proceeding
      case 3: // Batch Size
        return (
          !productFormData.batchSize ||
          productFormData.batchSize.trim() === "" ||
          parseFloat(productFormData.batchSize) <= 0
        );
      case 4: // Part Weight and Dimensions
        return (
          !productFormData.partWeightDimensions?.finishWeight ||
          !productFormData.partWeightDimensions?.partLength ||
          !productFormData.partWeightDimensions?.partWidth ||
          !productFormData.partWeightDimensions?.partHeight
        );
      case 5: // Part Details
        return false; // Part Details fields are optional, so always allow proceeding
      case 6: // Process Involved
        return false; // Process Involved fields are optional, so always allow proceeding
      case 7: // Manufacturing Country
        return !productFormData.manufacturingCountry;
      default:
        return false;
    }
  };

  const handleComplete = () => {
    onComplete?.(productFormData);
  };

  // Custom step change handler that validates before moving to next step
  const handleStepChange = async (newStep) => {
    // If moving forward from Material Selection step (step 1), validate first
    if (productChatStep === 1 && newStep > productChatStep) {
      if (materialSelectionRef.current && materialSelectionRef.current.validate) {
        const isValid = await materialSelectionRef.current.validate();
        if (!isValid) {
          // Validation failed, don't proceed to next step
          return;
        }
      }
    }
    // If moving forward from Material Grade step (step 2), validate grade if provided
    if (productChatStep === 2 && newStep > productChatStep) {
      if (materialGradeRef.current && materialGradeRef.current.validate && productFormData.materialGrade?.trim()) {
        const isValid = await materialGradeRef.current.validate();
        if (!isValid) {
          // Validation failed, don't proceed to next step
          return;
        }
      }
    }
    // Validation passed or not needed, proceed to next step
    setProductChatStep(newStep);
  };

  // Reset function to clear all fields and start from first question
  const handleReset = () => {
    setProductFormData({
      productName: "",
      materialSelection: "",
      materialGrade: "",
      batchSize: "",
      partWeightDimensions: {
        finishWeight: "",
        partLength: "",
        partWidth: "",
        partHeight: "",
      },
      partDetails: {
        // regrindPct: "",
        avgWallThickness: "",
        // runnerType: "",
        noOfCavities: "",
      },
      // marketPositioning: "",
      // skuWeight: "",
      // skuWeightUnit: "g",
      annualVolume: "",
      manufacturingCountry: "",
    });
    setIsMaterialValid(false);
    setIsValidatingMaterial(false);
    setProductChatStep(0);
    setDemoMode(false); // Reset demo toggle button
    if (onDemoModeChange) {
      onDemoModeChange(false); // Notify parent that demo mode is off
    }
    if (onReset) {
      onReset();
    }
  };

  // Handle demo toggle
  const handleDemoToggle = (checked) => {
    setDemoMode(checked);
    if (onDemoModeChange) {
      onDemoModeChange(checked);
    }
    if (checked) {
      // Set demo values when toggle is turned on
      setProductFormData({
        productName: "valve covers",
        materialSelection: "ADC12",
        materialGrade: "",
        batchSize: "9000",
        partWeightDimensions: {
          finishWeight: "200",
          partLength: "86",
          partWidth: "34",
          partHeight: "34",
        },
        partDetails: {
          avgWallThickness: "2",
          noOfCavities: "1",
        },
        processInvolved: {
          sandBlasting: true,
          deburing: true,
          heatTreatment: true,
        },
        annualVolume: "",
        manufacturingCountry: "China",
      });
      // Navigate to the last step to show all filled values
      setProductChatStep(7);
    } else {
      // Reset form when toggle is turned off
      handleReset();
    }
  };

  return (
    <QuestionsSection
      selectedValues={productSelectedValues}
      currentStep={productChatStep}
      onStepChange={handleStepChange}
      onComplete={handleComplete}
      brandColor="#ff6034"
      isNextDisabled={isNextDisabled}
      onReset={handleReset}
      demoMode={demoMode}
      onDemoToggle={handleDemoToggle}
    >
      <PartNameQuestion
        value={productFormData.productName}
        onChange={(value) => setProductFormData({ ...productFormData, productName: value })}
      />
      <MaterialSelectionQuestion
        ref={materialSelectionRef}
        value={productFormData.materialSelection}
        onChange={(value) => setProductFormData({ ...productFormData, materialSelection: value })}
        onValidationChange={(validationState) => {
          setIsMaterialValid(validationState.isMaterialValid || false);
          setIsValidatingMaterial(validationState.isValidating || false);
        }}
      />
      <MaterialGradeCastingQuestion
        ref={materialGradeRef}
        value={productFormData.materialGrade}
        materialType={productFormData.materialSelection}
        onChange={(value) => setProductFormData({ ...productFormData, materialGrade: value })}
        isMaterialValid={isMaterialValid}
        isValidatingMaterial={isValidatingMaterial}
      />
      <BatchSizeQuestion
        value={productFormData.batchSize}
        onChange={(value) => setProductFormData({ ...productFormData, batchSize: value })}
      />
      <PartWeightDimensionsQuestion
        value={productFormData.partWeightDimensions}
        onChange={(value) =>
          setProductFormData({ ...productFormData, partWeightDimensions: value })
        }
      />
      <PartDetailsQuestion
        value={productFormData.partDetails}
        onChange={(value) =>
          setProductFormData({ ...productFormData, partDetails: value })
        }
      />
      <ProcessInvolved
        value={productFormData.processInvolved}
        onChange={(value) =>
          setProductFormData({ ...productFormData, processInvolved: value })
        }
      />
      <ManufacturingCountrySelect
        value={productFormData.manufacturingCountry}
        onChange={(value) =>
          setProductFormData({ ...productFormData, manufacturingCountry: value })
        }
      />
    </QuestionsSection>
  );
}
